import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  NumberInput,
  TextInput,
  Textarea,
  Select,
  SegmentedControl,
  Button,
  Group,
  Stack,
  SimpleGrid,
  Divider,
  Text,
  Alert,
  Badge,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle } from "@tabler/icons-react";
import { PageHeader } from "../../components/common/PageHeader";
import { StatCard } from "../../components/common/StatCard";
import { DataTable, type Column } from "../../components/common/DataTable";
import { EntitySearchSelect } from "../../components/common/EntitySearchSelect";
import { UploadField } from "../../components/UploadField";
import {
  settlementsApi,
  type SettlementChannel,
  type SettlementDirection,
} from "../../api/settlements";
import { driversApi } from "../../api/drivers";
import { errorMessage } from "../../lib/axios";
import { formatMoney, formatDateTime } from "../../lib/format";
import { humanize } from "../../lib/labels";
import type { CommissionRecord } from "../../types/domain";

const CHANNELS: { value: SettlementChannel; label: string }[] = [
  { value: "cash", label: "Cash handover" },
  { value: "bank", label: "Bank transfer" },
  { value: "mobile-money", label: "Mobile money" },
  { value: "offset", label: "Offset against balance" },
];

interface FormValues {
  driverID: string;
  amount: number | "";
  direction: SettlementDirection;
  channel: SettlementChannel;
  reference: string;
  note: string;
  attachmentUrl: string;
}

const EMPTY: FormValues = {
  driverID: "",
  amount: "",
  direction: "payment",
  channel: "cash",
  reference: "",
  note: "",
  attachmentUrl: "",
};

export function SettlementsPage() {
  const qc = useQueryClient();
  const [v, setV] = useState<FormValues>(EMPTY);

  const commissionQuery = useQuery({
    queryKey: ["driver-commission", v.driverID],
    queryFn: () => settlementsApi.driverCommission(v.driverID, { limit: 20 }),
    enabled: Boolean(v.driverID),
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      settlementsApi.recordManual({
        driverID: v.driverID,
        amount: Number(v.amount),
        direction: v.direction,
        channel: v.channel,
        reference: v.reference || undefined,
        note: v.note || undefined,
        attachmentUrl: v.attachmentUrl || undefined,
      }),
    onSuccess: (result) => {
      const allocated = result.collection?.allocatedRecordIds.length ?? 0;
      notifications.show({
        color: "green",
        title: "Settlement recorded",
        message: allocated
          ? `Allocated to ${allocated} commission record(s). Balance is now ${formatMoney(result.platformDebt)}.`
          : `Balance is now ${formatMoney(result.platformDebt)}.`,
      });
      setV({ ...EMPTY, driverID: v.driverID });
      qc.invalidateQueries({ queryKey: ["driver-commission", v.driverID] });
      qc.invalidateQueries({ queryKey: ["transactions"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const balance = commissionQuery.data?.balance;
  const valid = Boolean(v.driverID) && Number(v.amount) > 0;

  const columns: Column<CommissionRecord>[] = [
    {
      key: "completedAt",
      header: "Trip completed",
      render: (r) => formatDateTime(r.completedAt),
    },
    {
      key: "paymentMode",
      header: "Paid by",
      render: (r) => humanize(r.paymentMode ?? ""),
    },
    {
      key: "platformCommission",
      header: "Commission",
      align: "right",
      render: (r) => formatMoney(r.platformCommission),
    },
    {
      key: "bookingFee",
      header: "Booking fee",
      align: "right",
      render: (r) => formatMoney(r.bookingFee),
    },
    {
      key: "latraLevy",
      header: "LATRA levy",
      align: "right",
      render: (r) => formatMoney(r.latraLevy ?? 0),
    },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <Badge
          color={r.status === "collected" ? "green" : "yellow"}
          variant="light"
        >
          {humanize(r.status ?? "")}
        </Badge>
      ),
    },
    {
      key: "collectedAt",
      header: "Collected",
      render: (r) =>
        r.collectedAt ? formatDateTime(r.collectedAt) : <Text c="dimmed">—</Text>,
    },
  ];

  return (
    <Stack>
      <PageHeader
        title="Manual Settlements"
        subtitle="Record payments made outside the payment gateway"
      />

      <Alert
        variant="light"
        color="orange"
        icon={<IconAlertTriangle size={16} />}
      >
        Entries post <strong>immediately</strong> with no approval step and move
        real money on the books. Your admin account is recorded against every
        entry. Attach evidence wherever possible.
      </Alert>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Card withBorder radius="md">
          <Stack>
            <Text size="sm" fw={600}>
              Record a settlement
            </Text>

            <EntitySearchSelect
              label="Driver"
              placeholder="Search by name or phone"
              value={v.driverID}
              onChange={(id) => setV({ ...v, driverID: id })}
              queryKey="drivers"
              required
              fetcher={async (search) => {
                const res = await driversApi.list({ search, limit: 20 });
                return res.items.map((d) => ({
                  value: d._id,
                  label: `${d.personalDetails?.name ?? "Driver"} — ${
                    d.personalDetails?.phoneNumber ?? ""
                  }`,
                }));
              }}
            />

            <div>
              <Text size="sm" fw={500} mb={4}>
                Direction
              </Text>
              <SegmentedControl
                fullWidth
                value={v.direction}
                onChange={(val) =>
                  setV({ ...v, direction: val as SettlementDirection })
                }
                data={[
                  { label: "Driver pays Smart Safiri", value: "payment" },
                  { label: "Smart Safiri pays driver", value: "payout" },
                ]}
              />
              <Text size="xs" c="dimmed" mt={4}>
                {v.direction === "payment"
                  ? "Reduces what the driver owes and collects commission, which issues a fiscal receipt."
                  : "Discharges what Smart Safiri owes the driver. No commission is collected."}
              </Text>
            </div>

            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <NumberInput
                label="Amount (TZS)"
                value={v.amount}
                onChange={(val) =>
                  setV({ ...v, amount: val === "" ? "" : Number(val) })
                }
                min={1}
                thousandSeparator=","
                required
              />
              <Select
                label="Channel"
                data={CHANNELS}
                value={v.channel}
                onChange={(val) =>
                  setV({ ...v, channel: (val as SettlementChannel) ?? "cash" })
                }
                allowDeselect={false}
              />
            </SimpleGrid>

            <TextInput
              label="Reference number"
              description="Receipt, voucher or bank reference"
              value={v.reference}
              onChange={(e) => setV({ ...v, reference: e.currentTarget.value })}
            />

            <Textarea
              label="Reason / note"
              placeholder="e.g. cash handover at branch office"
              value={v.note}
              onChange={(e) => setV({ ...v, note: e.currentTarget.value })}
              autosize
              minRows={2}
            />

            <UploadField
              label="Evidence (photo of receipt, bank slip)"
              value={v.attachmentUrl}
              onChange={(url) => setV({ ...v, attachmentUrl: url ?? "" })}
              folder="settlements"
            />

            <Group justify="flex-end">
              <Button
                loading={saveMutation.isPending}
                disabled={!valid}
                onClick={() => saveMutation.mutate()}
              >
                Record settlement
              </Button>
            </Group>
          </Stack>
        </Card>

        <Stack>
          {balance && (
            <SimpleGrid cols={2}>
              <StatCard
                label="Current balance"
                value={formatMoney(balance.platformDebt)}
                sub={
                  balance.platformDebt > 0
                    ? "Driver owes Smart Safiri"
                    : balance.platformDebt < 0
                      ? "Smart Safiri owes driver"
                      : "Settled"
                }
              />
              <StatCard
                label="Pending commission"
                value={formatMoney(balance.pendingCommission)}
                sub={`Limit ${formatMoney(balance.maxDebt)}`}
              />
            </SimpleGrid>
          )}

          {balance && balance.unallocatedCredit > 0 && (
            <Alert variant="light" color="blue">
              {formatMoney(balance.unallocatedCredit)} carried forward — not yet
              enough to cover the next whole commission record. It will be
              applied automatically on the next settlement.
            </Alert>
          )}

          {balance?.isLockedDueToDebt && (
            <Alert
              variant="light"
              color="red"
              icon={<IconAlertTriangle size={16} />}
            >
              This driver is locked for debt and will not be offered new trips
              until the balance clears.
            </Alert>
          )}

          <Card withBorder radius="md" p="xs">
            <Divider label="Commission ledger" labelPosition="left" mb="xs" />
            <DataTable<CommissionRecord>
              columns={columns}
              rows={commissionQuery.data?.records ?? []}
              rowKey={(r) => r._id}
              loading={commissionQuery.isLoading}
              emptyLabel={
                v.driverID
                  ? "No commission records for this driver"
                  : "Pick a driver to see their ledger"
              }
            />
          </Card>
        </Stack>
      </SimpleGrid>
    </Stack>
  );
}
